import { HttpClient, HttpParams } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ErrorHandlerService } from 'app/shared/error-handler.service';
import { TextService } from 'app/shared/text.service';
import { catchError, Observable } from 'rxjs';
import { FacetsResponse } from '../facet/facets-response';

@Injectable({
  providedIn: 'root',
})
export abstract class FacetCrudService<Model> {
  abstract readonly apiUrl: string;
  abstract readonly entityName: string;

  protected http = inject(HttpClient);
  private text = inject(TextService);
  private errorService = inject(ErrorHandlerService);
  protected destroyRef = inject(DestroyRef);
  selectedFacetSignal = signal<Model | null>(null);
  private selectedFacetImageSignal = signal<string | null | string[]>(null);

  readonly selectedFacet = this.selectedFacetSignal.asReadonly();
  readonly selectedFacetImage = this.selectedFacetImageSignal.asReadonly();

  setSelected(id: string | null) {
    if (!id) {
      this.selectedFacetSignal.set(null);
      return;
    }
    this.getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((facet) => this.selectedFacetSignal.set(facet));
  }

  save<FormModel>(chapter: FormModel, id: string | undefined) {
    if (id) {
      return this.update<FormModel>(chapter, id);
    } else {
      return this.create<FormModel>(chapter);
    }
  }

  getById(id: string): Observable<Model> {
    return this.http.get<Model>(`${this.apiUrl}/${id}`).pipe(catchError((err) => this.errorService.handle(err)));
  }

  getAll(params?: HttpParams): Observable<FacetsResponse<Model>> {
    return this.http
      .get<FacetsResponse<Model>>(`${this.apiUrl}`, { params: params })
      .pipe(catchError((err) => this.errorService.handle(err)));
  }

  delete(id: string) {
    return this.http.delete(`${this.apiUrl}/${id}`)
  }

  // private getImageUrlById(id: string): Observable<string | null> {
  //   return this.filesService.getFile(`${this.fileApiUrl}/${id}`).pipe(map((res) => res.url));
  // }

  private create<FormModel>(facet: FormModel) {
    return this.http
      .post<Model>(this.apiUrl, facet)
      .pipe(
        catchError((err) => this.errorService.handle(err, `${this.text.get('failed_create')} ${this.entityName}.`)),
      );
  }

  private update<FormModel>(facet: FormModel, id: string) {
    return this.http
      .patch<Model>(`${this.apiUrl}/${id}`, facet)
      .pipe(
        catchError((err) => this.errorService.handle(err, `${this.text.get('failed_update')} ${this.entityName}.`)),
      );
  }
}
